import type { ChatRepository, ChatChannelListInput, ChatMessageListInput, CreateChannelInput, SendMessageInput, TenantContext } from '@zetyar/data-contracts'
import type { Channel, Message, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

interface ChannelRow {
  readonly id: UUID
  readonly organization_id: UUID
  readonly name: string
  readonly description: string | null
  readonly type: string
  readonly created_at: string
  readonly updated_at: string
  readonly creator: { readonly id: UUID; readonly display_name: string } | null
}

function mapChannel(row: ChannelRow): Channel {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    description: row.description,
    type: row.type as Channel['type'],
    createdBy: row.creator?.id ?? '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: null,
  }
}

interface MessageRow {
  readonly id: UUID
  readonly channel_id: UUID
  readonly content: string
  readonly reply_to_id: UUID | null
  readonly is_edited: boolean
  readonly created_at: string
  readonly updated_at: string
  readonly sender: { readonly id: UUID; readonly display_name: string } | null
}

function mapMessage(row: MessageRow, organizationId: UUID): Message {
  return {
    id: row.id,
    channelId: row.channel_id,
    organizationId,
    content: row.content,
    replyToId: row.reply_to_id,
    isEdited: row.is_edited,
    editedAt: row.is_edited ? row.updated_at : null,
    senderId: row.sender?.id ?? '',
    sender: row.sender
      ? {
          id: row.sender.id,
          firebaseUid: '',
          email: '',
          displayName: row.sender.display_name,
          avatarUrl: null,
          phone: null,
          emailVerified: true,
          isSuperAdmin: false,
          status: 'active',
          lastLoginAt: null,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        }
      : undefined,
    createdAt: row.created_at,
    deletedAt: null,
  }
}

export function createSupabaseChatRepository(client: SupabaseClient): ChatRepository {
  return {
    async listChannels(context: TenantContext, { limit }: ChatChannelListInput) {
      const { data, error } = await client
        .from('channels')
        .select('id, organization_id, name, description, type, created_at, updated_at, creator:memberships!channels_created_by_fkey(id, display_name)')
        .eq('organization_id', context.organizationId)
        .order('name', { ascending: true })
        .limit(Math.max(1, Math.min(limit, 100)))

      if (error) throw error
      return (data as unknown as ChannelRow[]).map(mapChannel)
    },

    async listMessages(context: TenantContext, { channelId, limit }: ChatMessageListInput) {
      const { data, error } = await client
        .from('chat_messages')
        .select('id, channel_id, content, reply_to_id, is_edited, created_at, updated_at, sender:memberships!chat_messages_sender_membership_id_fkey(id, display_name)')
        .eq('channel_id', channelId)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 100)))

      if (error) throw error
      return (data as unknown as MessageRow[]).map((row) => mapMessage(row, context.organizationId))
    },

    async sendMessage(context: TenantContext, input: SendMessageInput) {
      if (!context.membershipId) throw new Error('Membership is required to send a message')

      const { data, error } = await client
        .from('chat_messages')
        .insert({
          channel_id: input.channelId,
          sender_membership_id: context.membershipId,
          content: input.content,
          reply_to_id: input.replyToId ?? null,
        })
        .select('id, channel_id, content, reply_to_id, is_edited, created_at, updated_at, sender:memberships!chat_messages_sender_membership_id_fkey(id, display_name)')
        .single()

      if (error) throw error
      return mapMessage(data as unknown as MessageRow, context.organizationId)
    },

    async createChannel(context: TenantContext, input: CreateChannelInput) {
      const { data, error } = await client
        .rpc('create_channel', {
          p_organization_id: context.organizationId,
          p_name: input.name,
          p_description: input.description ?? null,
          p_type: input.type ?? 'public',
        })
        .single()

      if (error) throw error
      const row = data as Record<string, unknown>
      return mapChannel({
        id: row.id as UUID,
        organization_id: row.organization_id as UUID,
        name: row.name as string,
        description: (row.description as string | null) ?? null,
        type: row.type as string,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
        creator: null,
      })
    },

    async deleteChannel(context: TenantContext, channelId: UUID) {
      const { error } = await client.rpc('delete_channel', {
        p_id: channelId,
        p_organization_id: context.organizationId,
      })
      if (error) throw error
    },

    async getUnreadChannelIds(context: TenantContext) {
      if (!context.membershipId) return []

      const { data: channels, error: channelsError } = await client
        .from('channels')
        .select('id, chat_messages(created_at)')
        .eq('organization_id', context.organizationId)
        .order('created_at', { referencedTable: 'chat_messages', ascending: false })
        .limit(1, { referencedTable: 'chat_messages' })

      if (channelsError) throw channelsError

      const { data: reads, error: readsError } = await client
        .from('channel_reads')
        .select('channel_id, last_read_at')
        .eq('membership_id', context.membershipId)

      if (readsError) throw readsError

      const lastReadByChannel = new Map((reads ?? []).map((r) => [r.channel_id as UUID, r.last_read_at as string]))

      return (channels ?? [])
        .filter((c) => {
          const lastMessage = (c as { chat_messages?: { created_at: string }[] }).chat_messages?.[0]
          if (!lastMessage) return false
          const lastRead = lastReadByChannel.get(c.id as UUID)
          return !lastRead || new Date(lastMessage.created_at) > new Date(lastRead)
        })
        .map((c) => c.id as UUID)
    },

    async markChannelRead(context: TenantContext, channelId: UUID) {
      if (!context.membershipId) throw new Error('Membership is required to mark a channel read')

      const { error } = await client
        .from('channel_reads')
        .upsert(
          { channel_id: channelId, membership_id: context.membershipId, last_read_at: new Date().toISOString() },
          { onConflict: 'channel_id,membership_id' },
        )

      if (error) throw error
    },

    async moderateMessage(context: TenantContext, messageId: UUID) {
      if (!context.membershipId) throw new Error('Membership is required to moderate a message')

      const { error } = await client.rpc('moderate_message', {
        p_id: messageId,
        p_organization_id: context.organizationId,
      })
      if (error) throw error
    },
  }
}
